import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { StudentDetailProfile, SessionStats } from '@/types/dashboard';
import { formatDate, formatPercent } from '@/lib/utils';

interface StudentCardProps {
  profile: StudentDetailProfile;
  sessionStats: SessionStats;
}

export function StudentCard({ profile, sessionStats }: StudentCardProps) {
  const name =
    [profile.first_name, profile.last_name].filter(Boolean).join(' ') ||
    profile.username ||
    '—';

  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle>Profile</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Name</span>
            <span className="font-medium">{name}</span>
          </div>
          {profile.email && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Email</span>
              <span>{profile.email}</span>
            </div>
          )}
          {profile.username && (
            <div className="flex justify-between">
              <span className="text-muted-foreground">Username</span>
              <span>@{profile.username}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="text-muted-foreground">Joined</span>
            <span>{formatDate(profile.joined_at)}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Current Streak</span>
            <Badge>{profile.current_streak}d</Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Longest Streak</span>
            <Badge variant="secondary">{profile.longest_streak}d</Badge>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Last Activity</span>
            <span>{profile.last_activity ? formatDate(profile.last_activity) : '—'}</span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Session Stats</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Total Sessions</span>
            <span className="font-medium">{sessionStats.total}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Completed</span>
            <span>{sessionStats.completed}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Skipped</span>
            <span>{sessionStats.skipped}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Completion Rate</span>
            <span className="font-medium">{formatPercent(sessionStats.completion_rate)}</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
